import useSWR from 'swr';
import { useAuth } from '@clerk/clerk-react';

import myShopClient from '@services/nearlive';

/**
 * Resolves whether the current user is on their first Unify session — i.e. they
 * have not yet dismissed the welcome screen — plus whether they are an
 * impersonating HQ staff member.
 *
 * `unifyFirstLoginAt` lives in Clerk `privateMetadata` (backend-only, never on
 * the session token), so it's read through `/api/users/welcome-state` rather
 * than a session claim. `isImpersonating` is resolved from the `act` claim
 * client-side; impersonated sessions never fetch the endpoint and are never
 * treated as a first session, so HQ "view as" never lands on `/welcome`.
 *
 * The fetch is best-effort: on error (e.g. the dev `?nockerk` Clerk bypass,
 * which has no token) we resolve to "not first" so the routing gate never traps
 * the user behind the welcome screen.
 */
export function useIsFirstSession() {
	const { isLoaded, sessionClaims } = useAuth();
	const isImpersonating = Boolean(sessionClaims?.act);

	const key = isLoaded && !isImpersonating ? 'unify:welcome-state' : null;
	const { data, error, mutate } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get('/api/users/welcome-state');
			return data;
		},
		{ revalidateOnFocus: false }
	);

	const dismiss = async () => {
		// Seed the cache with the dismissed state first so the routing gate sees
		// it immediately — the caller can navigate without a `/welcome` ⇄ `/`
		// bounce and without waiting on the ~2s persist roundtrip (NEA-5853).
		await mutate({ unifyFirstLoginAt: Date.now(), isImpersonating: false }, { revalidate: false });
		// Persist in the background, best-effort: if it fails the user sees the
		// welcome screen again next session, where the subscribe is an idempotent
		// no-op — strictly better than blocking or failing the handoff now.
		const client = await myShopClient();
		client.post('/api/users/dismiss-welcome').catch(() => {});
	};

	// Until Clerk loads, or the (non-impersonated) fetch resolves, we don't yet
	// know — the gate should wait rather than flash the dashboard or `/welcome`.
	const isResolving = !isLoaded || (key !== null && data === undefined && !error);
	const isFirstSession = !isImpersonating && !isResolving && !error && !data?.unifyFirstLoginAt;

	return { isFirstSession, isImpersonating, isResolving, dismiss };
}
